/**
 * Proofreading configuration (ADR-0002; D-0059: Harper, entirely in the
 * browser). What the browser's linter is told before its first lint, and the
 * Node test that proves the telling worked, share this module so they cannot
 * drift. Nothing here depends on Harper's package: the linter is described by
 * the three methods this module calls.
 *
 * The suppressed rules are the provisional list (OPEN 56 gives Beam the
 * curation): rules that would push a forbidden character (an em dash, an en
 * dash, an ellipsis character), rules that contradict the house style the
 * style engine already applies (spelled numbers, the Oxford comma either
 * way, double spaces, sentence capitals that the formatter handles), and
 * rules that judge medicolegal prose by the standards of a blog post (long
 * sentences, hedging, filler and discourse words, boring words).
 */
export const SUPPRESSED_PROOFREADING_RULES = [
    'Dashes',
    'NumericRangeEnDash',
    'UseEllipsisCharacter',
    'EllipsisLength',
    'OxfordComma',
    'NoOxfordComma',
    'Spaces',
    'SpelledNumbers',
    'SentenceCapitalization',
    'LongSentences',
    'Hedging',
    'FillerWords',
    'DiscourseMarkers',
    'BoringWords',
    'AvoidContractions',
    'UseTitleCase',
];
/**
 * Harper's own message, in the house typography. Its authors write with em
 * dashes and the ellipsis character, and the popover shows the message
 * verbatim, so the `ProgressiveNeedsBe` message alone puts three ellipsis
 * characters on the doctor's screen. Those characters are forbidden in this
 * product's text, which is the same reason `UseEllipsisCharacter`,
 * `EllipsisLength` and `Dashes` are suppressed above: what a rule must not
 * write into a report, its own wording must not show either.
 *
 * Words are quoted in backticks upstream and take the house quotes here. The
 * em dash becomes a comma rather than a colon because Harper uses it both as
 * an explanatory break and as a pair around an aside, and a comma is the only
 * substitution that reads correctly in both.
 *
 * A replacement is left exactly as Harper offers it. That text can enter the
 * report, so it is not quietly rewritten; the suppressed rules are what keep
 * it clean.
 */
export function houseMessage(message) {
    return message
        .replace(/`([^`]*)`/g, '"$1"')
        .replace(/…/g, '...')
        .replace(/\s*\u2014\s*/g, ', ')
        .replace(/\u2013/g, '-');
}
/**
 * Harper's quirk (recorded in D-0059): after the linter is constructed the
 * default lint configuration must be set explicitly before the first lint, or
 * the grammar rules stay silently inactive while spelling still works. This
 * does that, turns the suppressed rules off, and loads the dictionary.
 * Returns the configuration it applied, so a test can read it back.
 */
export async function configureProofreader(linter, dictionary) {
    const config = { ...(await linter.getDefaultLintConfig()) };
    for (const rule of SUPPRESSED_PROOFREADING_RULES)
        if (rule in config)
            config[rule] = false;
    await linter.setLintConfig(config);
    const words = [...new Set(dictionary.flatMap(dictionaryWords))];
    if (words.length > 0)
        await linter.importWords(words);
    return config;
}
/**
 * A dictionary entry is a term; Harper's dictionary takes words. "whole
 * person impairment" contributes its three words, each already English, and
 * "L4/5" contributes nothing a speller would flag.
 */
export function dictionaryWords(term) {
    return term
        .split(/[\s/]+/)
        .map((w) => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}'-]+$/gu, ''))
        .filter((w) => /\p{L}/u.test(w) && w.length >= 2);
}
//# sourceMappingURL=proofreading.js.map